import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputBase, Button, Checkbox, FormControlLabel, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useTheme from '../hooks/useTheme';
import ConversationModal from './ConversationModal';
import TableSkeleton from './TableSkeleton';
import { fetchWithCache, fetchAllPages, performanceMonitor, debounce, apiCache } from '../utils/apiCache';

export default function Discovery() {
  const theme = useTheme();
  const [discoveries, setDiscoveries] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDiscovery, setSelectedDiscovery] = useState(null);
  const [useDefaultFilter, setUseDefaultFilter] = useState(true);
  const [defaultFilterValue, setDefaultFilterValue] = useState('00:00:30'); // Default 30 seconds

  // Helper functions - defined before use
  const parseDurationToSeconds = (duration) => {
    if (!duration) return 0;
    const parts = duration.split(':').map(Number);
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  };

  const formatDuration = (duration) => {
    if (!duration) return '0:00:00';
    return duration;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '-';
    }
  };

  // Debounced search effect
  const debouncedSearchUpdate = useMemo(
    () => debounce((searchTerm) => {
      setDebouncedSearch(searchTerm);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearchUpdate(search);
  }, [search, debouncedSearchUpdate]);

  // Fetch discoveries with pagination and performance monitoring
  useEffect(() => {
    const fetchDiscoveries = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🚀 Starting paginated Discovery fetch...');
        const data = await performanceMonitor.measureApiCall(
          'Discovery/conversations-paginated',
          fetchAllPages('https://api.uxhq.com/conversations')
        );

        console.log(`📊 Discovery: Loaded ${data.length} total conversations`);
        
        // Ensure data is an array and sort by createdAt (most recent first)
        const discoveriesArray = Array.isArray(data) ? data : [];
        const sortedDiscoveries = discoveriesArray.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA; // Most recent first
        });
        setDiscoveries(sortedDiscoveries);
      } catch (err) {
        console.error('Error fetching discoveries:', err);
        setError('Failed to load discovery data. Please try again.');
        setDiscoveries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscoveries();
  }, []);

  // Memoized filtering for better performance
  const filteredDiscoveries = useMemo(() => {
    if (!Array.isArray(discoveries)) return [];
    
    return discoveries.filter(discovery => {
      // Use debounced search for filtering
      const matchesSearch = (discovery.name || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                           (discovery.user_email || '').toLowerCase().includes(debouncedSearch.toLowerCase());
      
      // Apply default filter if enabled
      if (useDefaultFilter) {
        const defaultFilterSeconds = parseDurationToSeconds(defaultFilterValue);
        const discoverySeconds = parseDurationToSeconds(discovery.duration || '0:00:00');
        if (discoverySeconds <= defaultFilterSeconds) return false;
      }
      
      return matchesSearch;
    });
  }, [discoveries, debouncedSearch, useDefaultFilter, defaultFilterValue]);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleViewChat = useCallback((discovery) => {
    setSelectedDiscovery({
      userId: discovery.user_id
    });
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedDiscovery(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Clear cache and fetch fresh paginated data
      console.log('🔄 Refreshing Discovery with fresh paginated data...');
      apiCache.clearAll(); // Clear all cache for fresh fetch
      
      const data = await performanceMonitor.measureApiCall(
        'Discovery/conversations-refresh-paginated',
        fetchAllPages('https://api.uxhq.com/conversations')
      );

      console.log(`📊 Discovery Refresh: Loaded ${data.length} total conversations`);
      
      const discoveriesArray = Array.isArray(data) ? data : [];
      const sortedDiscoveries = discoveriesArray.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      setDiscoveries(sortedDiscoveries);
    } catch (err) {
      console.error('Error refreshing discoveries:', err);
      setError('Failed to refresh discovery data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={theme.styles.pageContainer}>
        {/* Page Title */}
        <Typography variant="h4" sx={{ ...theme.styles.pageTitle, mb: 3 }}>
          DISCOVERY
        </Typography>

        {/* Filter and Search Bar Skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
              <FormControlLabel
                control={<Checkbox disabled />}
                label={
                  <Typography variant="body2" sx={{ color: theme.textSecondaryColor, fontWeight: 500 }}>
                    Only show chats longer than
                  </Typography>
                }
              />
            </Box>
          </Box>
          <Box sx={theme.styles.searchBar}>
            <SearchIcon sx={{ color: theme.textSecondaryColor, mr: 1 }} />
            <InputBase
              placeholder="search by name or email"
              disabled
              sx={theme.styles.searchInput}
            />
          </Box>
        </Box>

        {/* Table Skeleton */}
        <TableSkeleton 
          rows={8} 
          columns={['15%', '25%', '20%', '15%', '25%', '15%', '20%', '15%']} 
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={theme.styles.pageContainer}>
        <Typography variant="h4" sx={{ ...theme.styles.pageTitle, mb: 3 }}>
          DISCOVERY
        </Typography>
        <Card sx={{ ...theme.styles.card, p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={handleRefresh} sx={theme.styles.button}>
            Try Again
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={theme.styles.pageContainer}>
      {/* Page Title */}
      <Typography variant="h4" sx={{ ...theme.styles.pageTitle, mb: 3 }}>
        DISCOVERY
      </Typography>

      {/* Filter and Search Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Default Filter Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useDefaultFilter}
                  onChange={(e) => setUseDefaultFilter(e.target.checked)}
                  sx={{
                    color: theme.textSecondaryColor,
                    '&.Mui-checked': {
                      color: theme.primaryColor,
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: theme.textColor, fontWeight: 500 }}>
                  Only show chats longer than
                </Typography>
              }
            />
            {useDefaultFilter && (
              <InputBase
                value={defaultFilterValue}
                onChange={(e) => setDefaultFilterValue(e.target.value)}
                placeholder="00:00:30"
                sx={{
                  ...theme.styles.searchInput,
                  width: '100px',
                  fontSize: '14px',
                  textAlign: 'center',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={theme.styles.searchBar}>
          <SearchIcon sx={{ color: theme.textSecondaryColor, mr: 1 }} />
          <InputBase
            placeholder="search by name or email"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={theme.styles.searchInput}
          />
        </Box>
      </Box>

      {/* Discovery Chat Table */}
      <Card sx={theme.styles.card}>
        <TableContainer component={Paper} sx={{ ...theme.styles.card, borderRadius: 0 }}>
          <Table>
            <TableHead>
              <TableRow sx={theme.styles.tableHeader}>
                <TableCell sx={theme.styles.tableHeaderCell}>CID</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>EMAIL</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>NAME</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>COUNTRY</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>ROLE</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>DURATION</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>DATE/TIME</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>DISCOVERY CHAT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDiscoveries.length > 0 ? filteredDiscoveries.map((discovery, index) => (
                <TableRow key={discovery.discovery_id || index} hover>
                  <TableCell sx={theme.styles.tableCell}>{discovery.discovery_id || discovery.user_id || (4565 + index)}</TableCell>
                  <TableCell sx={theme.styles.tableCell}>{discovery.user_email || '-'}</TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {discovery.name && discovery.name.length > 20 ? `${discovery.name.substring(0, 20)}...` : discovery.name || '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>{discovery.country || '-'}</TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {discovery.role && discovery.role.length > 25 ? `${discovery.role.substring(0, 25)}...` : discovery.role || 'Product Designer Long Titl...'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {formatDuration(discovery.duration)}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {formatDateTime(discovery.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      sx={theme.styles.link}
                      onClick={() => handleViewChat(discovery)}
                    >
                      View Chat
                    </Typography>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', color: theme.textSecondaryColor, py: 4 }}>
                    No discovery chats available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      
      {/* Conversation Modal */}
      <ConversationModal
        open={modalOpen}
        onClose={handleCloseModal}
        userId={selectedDiscovery?.userId}
      />
    </Box>
  );
} 